
import { NavLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { VERSION as FRONTEND_VERSION } from './constants';
import sidebarMenus from './data/sidebar.json';
import { t, type TranslationKey } from './i18n/translations';
import { SidebarIcon, type SidebarIconName } from './components/SidebarIcon';
import { FamilyProfiles } from './components/FamilyProfiles';
import { AdviceNavigation } from './components/AdviceNavigation';
import './Sidebar.css';

type SidebarItem = {
  name: TranslationKey;
  nav: string;
  icon?: SidebarIconName;
};

type SidebarMenus = Record<string, SidebarItem[]>;

const defaultSidebarMode = 'modeLegacy';
const sidebarModeStorageKey = 'sidebarMode';
const appointmentChainResetEvent = 'appointment-chain-reset';
//const suppressLiveChatStorageKey = 'suppressLiveChat';
const typedSidebarMenus = sidebarMenus as SidebarMenus;

const getSidebarMode = () => {
  const storedSidebarMode = localStorage.getItem(sidebarModeStorageKey)?.trim();
  if (storedSidebarMode && typedSidebarMenus[storedSidebarMode]) {
    return storedSidebarMode;
  }

  localStorage.setItem(sidebarModeStorageKey, defaultSidebarMode);
  return defaultSidebarMode;
};

const readStoredUserName = () => {
  const fullName = localStorage.getItem('userFullName')?.trim();
  const loginUserName = localStorage.getItem('loginUserName')?.trim();
  return fullName || loginUserName || '';
};

const readSuppressLiveChat = () => localStorage.getItem('suppressLiveChat')?.trim().toLowerCase() === 'true';

const capitalizeFirstCharacter = (value: string) => {
  if (!value) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

export function Sidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [userName, setUserName] = useState(readStoredUserName);
  const [sidebarMode, setSidebarMode] = useState(getSidebarMode);
  const [suppressLiveChat, setSuppressLiveChat] = useState(readSuppressLiveChat);
  const menuItems = typedSidebarMenus[sidebarMode] ?? typedSidebarMenus[defaultSidebarMode] ?? [];
  const isAdminSidebar = sidebarMode === 'modeAdmin' || sidebarMode === 'modeRoot';
  const isPatientMobilePage = sidebarMode === 'modePatient' &&
    ['/dashboard', '/appointments', '/documents', '/instructions', '/instructions/homebleaching', '/profile'].includes(pathname);
  const hideLiveChat = suppressLiveChat || isAdminSidebar;

  const displayedUserName = userName || 'Benutzer';

  const logout = () => {

    console.log('logout');

    localStorage.setItem('loginPatientId', '');

    if (sidebarMode === 'modePatient') {
      ['token', 'loginUserId', 'loginUserName', 'userFullName', 'tfaMode', 'customerId',
        'loginAdminLevel', 'selectedPatientId', 'selectedAppointmentId', 'pendingAppointmentFull',
        'pendingCancellationAppointment', 'lastCancelledAppointment', 'lastMovedAppointment',
        'lastBookedAppointmentSummary', 'currentAppointmentChain'].forEach((key) => localStorage.removeItem(key));
    }

    navigate('login');
  };

  const callPhone = () => {
    console.log('callPhone');
  };

  const liveChat = () => {
    console.log('liveChat');
  };

  const handleMenuItemClick = (item: SidebarItem) => {
    localStorage.setItem('previousPage', ' ');

    if (isPatientMobilePage && window.matchMedia('(max-width: 760px)').matches) {
      window.scrollTo(0, 0);
    }

    if (item.nav === 'appointment-chain') {
      localStorage.removeItem('currentAppointmentChain');
      localStorage.removeItem('selectedPatientId');
      localStorage.removeItem('selectedDateTime');
      localStorage.removeItem('selectedStartTime');
      window.dispatchEvent(new Event(appointmentChainResetEvent));
    }
  };

  useEffect(() => {
    const syncUserName = () => setUserName(readStoredUserName());

    window.addEventListener('storage', syncUserName);
    window.addEventListener('user-name-updated', syncUserName);
    return () => {
      window.removeEventListener('storage', syncUserName);
      window.removeEventListener('user-name-updated', syncUserName);
    };
  }, []);

  useEffect(() => {
    const syncSidebarMode = () => {
      setSidebarMode(getSidebarMode());
    };

    window.addEventListener('storage', syncSidebarMode);
    window.addEventListener('sidebar-mode-updated', syncSidebarMode);
    return () => {
      window.removeEventListener('storage', syncSidebarMode);
      window.removeEventListener('sidebar-mode-updated', syncSidebarMode);
    };
  }, []);

  useEffect(() => {
    const syncSuppressLiveChat = () => setSuppressLiveChat(readSuppressLiveChat());

    window.addEventListener('storage', syncSuppressLiveChat);
    window.addEventListener('suppress-live-chat-updated', syncSuppressLiveChat);
    return () => {
      window.removeEventListener('storage', syncSuppressLiveChat);
      window.removeEventListener('suppress-live-chat-updated', syncSuppressLiveChat);
    };
  }, []);

  const contactCard = !hideLiveChat ? (
    <div className="portal-sidebar__contact flex flex-1 flex-col justify-center pb-12">
      <div className="rounded-[28px] border border-[#f1ede7] bg-[#fcfbf8] px-4 py-5 text-sm text-slate-500">
        {sidebarMode === 'modePatient' ? <>
          <a className="portal-sidebar__phone-card" href="tel:+41526254625" aria-label="052 625 46 25 anrufen" />
          <div className="portal-sidebar__phone-content flex w-full items-center gap-3 rounded-2xl text-left">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#fcfbf8] text-[#c7b693]">
              <SidebarIcon name="phone" className="h-4 w-4" />
            </span>
            <span>052 625 46 25</span>
          </div>
        </> : <button
          type="button"
          onClick={callPhone}
          className="flex w-full items-center gap-3 rounded-2xl text-left transition hover:bg-[#f7f2ea] focus:outline-none focus:ring-2 focus:ring-[#e7d8b8] focus:ring-offset-2 focus:ring-offset-[#fcfbf8]"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#fcfbf8] text-[#c7b693]">
            <SidebarIcon name="phone" className="h-4 w-4" />
          </span>
          <span>052 625 46 25</span>
        </button>}
        {isPatientMobilePage && (
          <a className="portal-sidebar__mobile-only portal-sidebar__phone" href="tel:+41526254625">
            <SidebarIcon name="phone" className="h-4 w-4 shrink-0" />
            <span>052 625 46 25</span>
          </a>
        )}
        {/* <button
          type="button"
          onClick={liveChat}
          className="mt-4 flex w-full items-center gap-3 rounded-2xl text-left transition hover:bg-[#f7f2ea] focus:outline-none focus:ring-2 focus:ring-[#e7d8b8] focus:ring-offset-2 focus:ring-offset-[#fcfbf8]"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#fcfbf8] text-[#c7b693]">
            <SidebarIcon name="chat" className="h-4 w-4" />
          </span>
          <span>{capitalizeFirstCharacter(t('sidebar.live-chat'))}</span>
        </button> */}
      </div>
    </div>
  ) : null;

  return (
    <aside className={`portal-sidebar${sidebarMode === 'modePatient' ? ' portal-sidebar--patient' : ''}${isPatientMobilePage ? ' portal-sidebar--patient-mobile' : ''} flex min-h-full w-64 self-stretch px-5 py-6 text-slate-700`}>
      <div className="portal-sidebar__body flex w-full flex-1 flex-col">
        <div className="portal-sidebar__scroll">
          <nav className="portal-sidebar__nav mt-8 flex flex-col gap-2 text-sm font-medium">
            {menuItems.map((item) => (
              sidebarMode === 'modePatient' && item.nav === 'instructions'
                ? <AdviceNavigation key="patient-advice" /> : <NavLink
                key={`${sidebarMode}-${item.nav}`}
                to={item.nav}
                onClick={() => handleMenuItemClick(item)}
                className={({ isActive }) =>
                  `${item.nav === 'documents' ? 'portal-sidebar__documents-link ' : ''}flex items-center gap-3 rounded-2xl px-4 py-3 transition ${isActive
                    ? 'bg-[#f7f2ea] text-[#c9ae78]'
                    : 'text-slate-400 hover:bg-[#f7f2ea] hover:text-slate-700'
                  }`
                }
              >
                {item.nav === 'profile' ? (
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="m19.4 13 .1-1-.1-1 2.1-1.6-2-3.4-2.5 1a8 8 0 0 0-1.7-1L15 3h-4l-.4 2.9a8 8 0 0 0-1.7 1l-2.5-1-2 3.4L6.5 11l-.1 1 .1 1-2.1 1.6 2 3.4 2.5-1a8 8 0 0 0 1.7 1L11 21h4l.4-2.9a8 8 0 0 0 1.7-1l2.5 1 2-3.4-2.2-1.7ZM13 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7Z" />
                  </svg>
                ) : (
                  <SidebarIcon name={item.nav === 'appointments' ? 'calendar' : item.icon} className="h-4 w-4 shrink-0" />
                )}
                <span className={isPatientMobilePage ? 'portal-sidebar__desktop-label' : undefined}>{item.nav === 'admin-booking' ? 'Einzeltermin' : capitalizeFirstCharacter(t(item.name))}</span>
                {isPatientMobilePage && (
                  <span className="portal-sidebar__mobile-only">
                    {item.nav === 'appointments' ? 'Termine' : item.nav === 'profile' ? 'Profil' : capitalizeFirstCharacter(t(item.name))}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {sidebarMode !== 'modePatient' && contactCard}

        </div>

        {sidebarMode === 'modePatient' && contactCard}

        <div className="portal-sidebar__footer">
          {sidebarMode === 'modePatient' ? (
            <FamilyProfiles primaryName={displayedUserName}
              primaryProfileId={localStorage.getItem('loginPatientId')?.trim() || 'primary'} onLogout={logout} />
          ) : <button
            type="button"
            onClick={logout}
            className="portal-sidebar__user w-full rounded-[28px] border border-[#efe7db] bg-[#f7f2ea] px-4 py-4 text-left text-sm text-slate-500 shadow-[0_16px_35px_rgba(30,41,59,0.03)] transition hover:bg-[#f4ede1] focus:outline-none focus:ring-2 focus:ring-[#e7d8b8] focus:ring-offset-2 focus:ring-offset-[#fcfbf8]"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#ece4d6] bg-[#fcfbf8] text-[#b7aca0]">
                <SidebarIcon name="profile" className="h-4 w-4" />
              </span>
              <p className="flex-1 truncate text-sm font-semibold text-slate-700">{displayedUserName}</p>
              {isPatientMobilePage && <span className="portal-sidebar__mobile-only">Abmelden</span>}
              <span className="text-[#d7cec1]">
                <SidebarIcon name="logout" className="h-4 w-4" />
              </span>
            </div>
          </button>}
          {FRONTEND_VERSION.trim() ? (
            <p className="portal-sidebar__version mt-3 px-1 text-right text-[10px] text-[#cfc4b5]">
              Version {FRONTEND_VERSION}
            </p>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
